// src/pages/DashboardPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';

const StoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const BoxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const TrendingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null); // aggregated totals
  const [storeAnalyticsMap, setStoreAnalyticsMap] = useState({}); // storeId → views
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    api.getStores().then(data => {
      const owned = data.filter(s => s.ownerId === user?.storeOwner?.id);
      setStores(owned);
      return owned;
    }).then(owned => {
      if (!owned.length) return;
      setAnalyticsLoading(true);
      Promise.allSettled(owned.map(s => api.getAnalytics(s.id).then(d => ({ storeId: s.id, data: d }))))
        .then(results => {
          const totals = { storeViews: 0, storeUniqueVisitors: 0, productViews: 0, productUniqueVisitors: 0, dealLocks: 0 };
          const map = {};
          results.forEach(r => {
            if (r.status !== 'fulfilled') return;
            const { storeId, data } = r.value;
            totals.storeViews += data.storeViews;
            totals.storeUniqueVisitors += data.storeUniqueVisitors;
            totals.productViews += data.productViews.reduce((s, x) => s + x.views, 0);
            totals.productUniqueVisitors += data.productViews.reduce((s, x) => s + x.uniqueVisitors, 0);
            totals.dealLocks += data.dealLocks.reduce((s, x) => s + x.locks, 0);
            map[storeId] = data.storeViews;
          });
          setAnalytics(totals);
          setStoreAnalyticsMap(map);
        })
        .finally(() => setAnalyticsLoading(false));
    }).finally(() => setLoading(false));
  }, [user]);

  const totalCoupons = stores.reduce((acc, s) => acc + (s._count?.coupons ?? 0), 0);
  const totalProducts = stores.reduce((acc, s) => acc + (s._count?.products ?? 0), 0);
  const firstName = user?.storeOwner?.name?.split(' ')[0] || 'there';

  const StatValue = ({ value }) => analyticsLoading
    ? <div style={{ height: 36, width: 48, background: 'var(--border)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
    : <div className="stat-value">{value ?? '—'}</div>;

  return (
    <div className="dash-root">
      <Sidebar active="dashboard" />
      <main className="dash-main">
        <div className="page-content">

          <div className="page-header">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                Good day, {firstName}
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 2 }}>
                Here's what's happening across your stores
              </p>
            </div>
          </div>

          {/* Section: Engagement */}
          <div style={{ marginBottom: 6 }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Engagement
            </p>
            <div className="stat-grid">
              <div className="stat-card">
                <div className="stat-icon indigo"><EyeIcon /></div>
                <StatValue value={analytics?.storeViews} />
                <div className="stat-label">Store Views</div>
                {!analyticsLoading && analytics && (
                  <div className="stat-sub">{analytics.storeUniqueVisitors} unique visitors</div>
                )}
              </div>
              <div className="stat-card">
                <div className="stat-icon green"><TrendingIcon /></div>
                <StatValue value={analytics?.productViews} />
                <div className="stat-label">Product Views</div>
                {!analyticsLoading && analytics && (
                  <div className="stat-sub">{analytics.productUniqueVisitors} unique visitors</div>
                )}
              </div>
              <div className="stat-card">
                <div className="stat-icon amber"><LockIcon /></div>
                <StatValue value={analytics?.dealLocks} />
                <div className="stat-label">Deal Locks</div>
                {!analyticsLoading && analytics && (
                  <div className="stat-sub">Total redemptions</div>
                )}
              </div>
            </div>
          </div>

          {/* Section: Inventory */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Inventory
            </p>
            <div className="stat-grid">
              <div className="stat-card">
                <div className="stat-icon indigo"><StoreIcon /></div>
                <div className="stat-value">{stores.length}</div>
                <div className="stat-label">Stores</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon green"><TagIcon /></div>
                <div className="stat-value">{totalCoupons}</div>
                <div className="stat-label">Active Coupons</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon amber"><BoxIcon /></div>
                <div className="stat-value">{totalProducts}</div>
                <div className="stat-label">Products Listed</div>
              </div>
            </div>
          </div>

          {/* Feature shortcuts */}
          <div className="section-header" style={{ marginBottom: 12 }}>
            <h2>Manage</h2>
          </div>
          <div className="table-card">
            {[
              {
                icon: <TagIcon />, bg: 'var(--success-light)', color: 'var(--success)',
                label: 'Coupons',
                meta: `${totalCoupons} active deal${totalCoupons !== 1 ? 's' : ''}`,
                actionLabel: 'New', path: '/coupons',
              },
              {
                icon: <BoxIcon />, bg: 'var(--warning-light)', color: 'var(--warning)',
                label: 'Products',
                meta: `${totalProducts} listed across ${stores.length} store${stores.length !== 1 ? 's' : ''}`,
                actionLabel: 'Add', path: '/products',
              },
              {
                icon: <TrendingIcon />, bg: 'var(--primary-light)', color: 'var(--primary)',
                label: 'Analytics',
                meta: analytics
                  ? `${analytics.storeViews} store views · ${analytics.dealLocks} deal locks`
                  : analyticsLoading ? 'Loading…' : 'No data yet',
                actionLabel: null, path: '/analytics',
              },
            ].map(({ icon, bg, color, label, meta, actionLabel, path }, i, arr) => (
              <div key={label} className="dash-shortcut-row"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}
                onClick={() => navigate(path)}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</div>
                  <div style={{ fontSize: '0.775rem', color: 'var(--text-muted)', marginTop: 2 }}>{meta}</div>
                </div>
                {actionLabel && (
                  <button className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '0.78rem', flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); navigate(path, { state: { openNew: true } }); }}>
                    <PlusIcon /> {actionLabel}
                  </button>
                )}
                <ChevronRight />
              </div>
            ))}
          </div>

        </div>
      </main>
    </div>
  );
}
